import { createMuiTheme, makeStyles } from '@material-ui/core';

const theme = createMuiTheme({
    palette: {
        primary: { main: '#9FE76E' },
    },
    typography: { fontFamily: "'Inter', sans-serif" },
});

const useStyles = makeStyles((theme) => ({
    backdrop: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
    dialogPaper: {
        borderRadius: 24,
        maxWidth: 600,
        margin: 16,
        [theme.breakpoints.down('sm')]: {
            margin: 0,
            maxWidth: '100%',
            borderRadius: '24px 24px 0 0',
        },
    },
    header: {
        padding: theme.spacing(3),
        borderBottom: '1px solid #f0f0f0',
    },
    handleBar: {
        width: 48,
        height: 4,
        backgroundColor: '#d1d5db',
        borderRadius: 2,
        margin: '0 auto 16px',
    },
    closeButton: {
        backgroundColor: '#f3f4f6',
        '&:hover': { backgroundColor: '#e5e7eb' },
    },
    content: { padding: theme.spacing(3) },
    freeCard: {
        backgroundColor: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: 12,
        padding: theme.spacing(3),
        margin: '0 auto 16px',
    },
    proCard: {
        backgroundColor: '#ffffff',
        border: '2px solid #F9D783',
        borderRadius: 12,
        padding: theme.spacing(3),
        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
        position: 'relative',
        marginTop: theme.spacing(2),
        zIndex: 1,
    },
    proBadgeContainer: {
        width: '80px',
        margin: '0 auto 16px',
        position: 'relative',
        zIndex: 9, // Increased to ensure it's above other elements
    },
    proBadge: {
        backgroundColor: '#F9D783',
        color: '#92400e',
        fontWeight: 'bold',
        padding: '4px 16px',
        borderRadius: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        position: 'absolute',
        top: '-12px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 10, // Increased to ensure badge is above its container
    },
    billingToggle: {
        backgroundColor: '#f3f4f6',
        borderRadius: 12,
        padding: 4,
        display: 'flex',
    },
    toggleButton: {
        flex: 1,
        padding: '8px 12px',
        border: 'none',
        borderRadius: 8,
        fontSize: 14,
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&.active': {
            backgroundColor: '#ffffff',
            color: '#1E1E1E',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        },
        '&.inactive': { backgroundColor: 'transparent', color: '#6b7280' },
    },
    savingsBadge: {
        backgroundColor: '#d1fae5',
        color: '#065f46',
        border: '1px solid #6ee7b7',
        borderRadius: 8,
        padding: theme.spacing(1.5),
        marginTop: theme.spacing(1),
    },
    actionButton: {
        backgroundColor: '#F9D783',
        color: '#92400e',
        fontWeight: 'bold',
        padding: '16px 24px',
        borderRadius: 12,
        textTransform: 'none',
        fontSize: 16,
        boxShadow: '0 4px 12px rgba(249, 215, 131, 0.3)',
        '&:hover': {
            backgroundColor: '#fbbf24',
            boxShadow: '0 6px 16px rgba(249, 215, 131, 0.4)',
        },
    },
    secondaryButton: {
        border: '1px solid #d1d5db',
        color: '#1E1E1E',
        fontWeight: 500,
        padding: '16px 24px',
        borderRadius: 12,
        textTransform: 'none',
        fontSize: 16,
        '&:hover': { backgroundColor: '#f9fafb' },
    },
    paymentHeader: {
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #e5e7eb',
        padding: theme.spacing(2),
    },
    planSummary: {
        backgroundColor: 'rgba(249, 215, 131, 0.2)',
        borderRadius: 12,
        padding: theme.spacing(2),
    },
    paymentOption: {
        border: '1px solid #e5e7eb',
        borderRadius: 12,
        overflow: 'hidden',
        marginBottom: theme.spacing(2),
    },
    paymentButton: {
        width: '100%',
        padding: theme.spacing(2),
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        textTransform: 'none',
        '&:hover': { backgroundColor: '#f9fafb' },
    },
    expandedContent: {
        backgroundColor: '#f9fafb',
        borderTop: '1px solid #e5e7eb',
        padding: theme.spacing(2),
    },
    infoBox: {
        borderRadius: 12,
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    fixedBottom: {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#ffffff',
        borderTop: '1px solid #e5e7eb',
        padding: theme.spacing(2),
        zIndex: 1000,
    },
    successIcon: {
        width: 96,
        height: 96,
        backgroundColor: '#d1fae5',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 24px',
    },
    featureIcon: {
        width: 48,
        height: 48,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    paymentPlanBadgeContainer: {
        backgroundColor: '#FEF7E5',
        padding: '12px',
        borderRadius: '8px',
        display: 'flex',
        gap: '8px',
        alignItems: 'center'
    },
    paymentPlanBadge: {
        borderRadius: '100%',
        padding: '4px',
        width: '32px',
        height: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#875011',
    },
    paymentPlanName: {
        fontWeight: 'bold',
        color: '#000000',
        fontSize: '16px',
    },
    paymentPlanPrice: {
        color: '#C0C1BE',
        fontSize: '14px',
    },
    paymentPlanPriceDiscounted: {
        color: '#C0C1BE',
        textDecoration: 'line-through',
        fontSize: '14px',
    },
    paymentPlanDiscount: {
        color: '#4B8D5D',
        fontSize: '14px',
    }
}));

export { useStyles, theme };
